// SETTINGS > USERS -- ADMIN ONLY (settings-users-tab 2026-09-23; user
// management added 2026-09-30, owner decision).
//
// Name, login, role, active and last sign-in for every internal user, and --
// for the Admin who sees this tab -- Add user, Edit (name, login, role),
// Deactivate and Reactivate. There is NO delete: a user is only ever
// deactivated, so history and attribution survive.
//
// ⚠️ THE SCREEN IS NOT THE GUARD. The tab is hidden for Sales and Viewer, but
// every route (/users, /create_user, /update_user, /set_user_active) refuses
// them with a 403 whatever this does, and the server enforces every rule: no
// self role change or self deactivation, never demote or deactivate the last
// active Admin, unique login, password minimum. This screen only avoids
// offering what the server would refuse, and shows its refusal verbatim.
//
// ⚠️ THE INITIAL PASSWORD is typed once, sent once, and cleared from state as
// soon as the request returns. It is never shown again and never logged.
//
// `last sign-in` is the stored record every successful sign-in writes, kept
// 12 months (login-history-12-months), shown in the viewer's own clock.

import { useCallback, useEffect, useState } from "react";
import {
  listUsers, createUser, updateUser, setUserActive, resetUserPassword, type AppUser, type UserRole,
} from "@/services/api";
import { useSessionUidOrNull } from "@/auth/AuthContext";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const CELL: React.CSSProperties = {
  padding: `${spacing[8]}px ${spacing[12]}px`, fontSize: typography.fontSize.sm,
  color: semanticColors.textPrimary, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", textAlign: "left",
};
const HEAD: React.CSSProperties = {
  ...CELL, color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium,
  fontSize: typography.fontSize.xs, backgroundColor: semanticColors.panelBg,
};
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box", minWidth: 0,
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[12]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};

const ROLE_LABEL: Record<string, string> = { admin: "Admin", sales: "Sales", viewer: "Viewer" };
const ROLES: UserRole[] = ["admin", "sales", "viewer"];
/** Mirrors the server's minimum (auth_password_policy, never below 10). */
export const MIN_PASSWORD = 10;

/** A stored UTC timestamp ("YYYY-MM-DD HH:MM:SS") in the viewer's clock. */
export function formatSignIn(utc: string | null): string {
  if (!utc) return "Never";
  const d = new Date(utc.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return utc;
  const pad = (n: number) => String(n).padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // P3-8: the same "24 Sep 2026" every other screen uses, plus the time.
  return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RoleSelect({ value, onChange, testid, disabled }: {
  value: UserRole; onChange: (r: UserRole) => void; testid: string; disabled?: boolean;
}) {
  return (
    <select data-testid={testid} value={value} disabled={disabled}
            onChange={(e) => onChange(e.target.value as UserRole)} style={INPUT}>
      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
    </select>
  );
}

function AddUserForm({ onDone, onCancel }: { onDone: (u: AppUser) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [role, setRole] = useState<UserRole>("sales");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("A user needs a name."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(login.trim())) { setError("The login must be an e-mail address."); return; }
    if (password.length < MIN_PASSWORD) { setError(`The password must be at least ${MIN_PASSWORD} characters.`); return; }
    setBusy(true);
    const result = await createUser({ name: name.trim(), login: login.trim(), role, password });
    setBusy(false);
    // Never kept: cleared whatever the answer.
    setPassword("");
    if (!result.ok || !result.user) { setError(result.error ?? "The user was not created."); return; }
    onDone(result.user);
  };

  return (
    <div data-testid="user-add-form" style={{
      display: "flex", flexWrap: "wrap", gap: spacing[8], alignItems: "center",
      padding: spacing[12], marginBottom: spacing[12], borderRadius: radius.base,
      border: `1px solid ${semanticColors.border}`, backgroundColor: semanticColors.panelBg,
    }}>
      <input data-testid="user-add-name" placeholder="Name" value={name}
             onChange={(e) => setName(e.target.value)} style={{ ...INPUT, width: 180 }} />
      <input data-testid="user-add-login" placeholder="Login (e-mail)" value={login}
             onChange={(e) => setLogin(e.target.value)} style={{ ...INPUT, width: 220 }} />
      <RoleSelect testid="user-add-role" value={role} onChange={setRole} />
      <input data-testid="user-add-password" type="password" autoComplete="new-password"
             placeholder={`Initial password (min ${MIN_PASSWORD})`} value={password}
             onChange={(e) => setPassword(e.target.value)} style={{ ...INPUT, width: 220 }} />
      <button data-testid="user-add-submit" disabled={busy} onClick={() => void submit()} style={PRIMARY}>
        {busy ? "Adding…" : "Add user"}
      </button>
      <button data-testid="user-add-cancel" onClick={onCancel} style={BUTTON}>Cancel</button>
      {error && (
        <div data-testid="user-add-error" role="alert"
             style={{ width: "100%", fontSize: typography.fontSize.sm, color: semanticColors.destructive }}>
          {error}
        </div>
      )}
    </div>
  );
}

export function UsersTab() {
  const myUid = useSessionUidOrNull();
  const [users, setUsers] = useState<AppUser[] | null>(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ name: string; login: string; role: UserRole }>({ name: "", login: "", role: "sales" });
  const [rowError, setRowError] = useState<{ id: number; text: string } | null>(null);
  const [note, setNote] = useState("");
  // password-reset: the row being reset and the typed password. Cleared as
  // soon as the request returns, whatever the answer.
  const [resetting, setResetting] = useState<number | null>(null);
  const [resetPw, setResetPw] = useState("");

  const load = useCallback(async () => {
    const result = await listUsers();
    if (!result.ok || !result.users) {
      setUsers(null);
      setError(result.error ?? "The user list could not be read.");
      return;
    }
    setError("");
    setUsers(result.users);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (u: AppUser) => {
    setRowError(null); setNote("");
    setEditing(u.id);
    setDraft({ name: u.name, login: u.login, role: u.role ?? "viewer" });
  };

  const saveEdit = async (u: AppUser) => {
    const changes: { name?: string; login?: string; role?: UserRole } = {};
    if (draft.name.trim() !== u.name) changes.name = draft.name.trim();
    if (draft.login.trim() !== u.login) changes.login = draft.login.trim();
    if (draft.role !== u.role) changes.role = draft.role;
    if (Object.keys(changes).length === 0) { setEditing(null); return; }
    const result = await updateUser(u.id, changes);
    if (!result.ok) { setRowError({ id: u.id, text: result.error ?? "Not saved." }); return; }
    setEditing(null);
    setNote(`Saved ${result.user?.login ?? u.login}.`);
    void load();
  };

  const toggleActive = async (u: AppUser) => {
    setRowError(null); setNote("");
    const result = await setUserActive(u.id, !u.active);
    if (!result.ok) { setRowError({ id: u.id, text: result.error ?? "Not changed." }); return; }
    setNote(`${u.login} ${u.active ? "deactivated" : "reactivated"}.`);
    void load();
  };

  const saveReset = async (u: AppUser) => {
    setRowError(null); setNote("");
    if (resetPw.length < MIN_PASSWORD) {
      setRowError({ id: u.id, text: `The password must be at least ${MIN_PASSWORD} characters.` });
      return;
    }
    const result = await resetUserPassword(u.id, resetPw);
    setResetPw("");
    if (!result.ok) { setRowError({ id: u.id, text: result.error ?? "Not changed." }); return; }
    setResetting(null);
    setNote(`Password reset for ${u.login}. Their open sessions were signed out.`);
  };

  if (error) {
    return (
      <div data-testid="users-error" role="alert" style={{
        padding: `${spacing[8]}px ${spacing[12]}px`, borderRadius: radius.base,
        backgroundColor: semanticColors.destructiveBg, border: `1px solid ${semanticColors.destructiveBorder}`,
        color: semanticColors.destructive, fontSize: typography.fontSize.sm,
      }}>
        {error}
      </div>
    );
  }
  if (!users) {
    return <div data-testid="users-loading" style={{ color: semanticColors.textSecondary }}>Reading users…</div>;
  }

  return (
    <div data-testid="users-tab">
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: spacing[12], marginBottom: spacing[8] }}>
        <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, flex: 1, minWidth: 240 }}>
          Admins add users, change roles and deactivate accounts here. Users are never
          deleted. Sign-ins are kept for 12 months.
        </div>
        {!adding && (
          <button data-testid="user-add-open" style={PRIMARY} onClick={() => { setAdding(true); setNote(""); }}>
            Add user
          </button>
        )}
      </div>
      {adding && (
        <AddUserForm
          onCancel={() => setAdding(false)}
          onDone={(u) => { setAdding(false); setNote(`Added ${u.login}.`); void load(); }}
        />
      )}
      {note && <div data-testid="users-note" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>{note}</div>}
      <div style={{ border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, overflowX: "auto" }}>
        <table data-testid="users-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={HEAD}>Name</th>
              <th style={HEAD}>Login</th>
              <th style={HEAD}>Role</th>
              <th style={HEAD}>Active</th>
              <th style={HEAD}>Last sign-in</th>
              <th style={HEAD} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = myUid === u.id;
              const isEditing = editing === u.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td style={CELL}>
                    {isEditing
                      ? <input data-testid={`user-edit-name-${u.id}`} value={draft.name}
                               onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={{ ...INPUT, width: 160 }} />
                      : u.name}
                  </td>
                  <td style={CELL}>
                    {isEditing
                      ? <input data-testid={`user-edit-login-${u.id}`} value={draft.login}
                               onChange={(e) => setDraft({ ...draft, login: e.target.value })} style={{ ...INPUT, width: 200 }} />
                      : u.login}
                  </td>
                  <td style={CELL} data-testid={`user-role-${u.id}`}>
                    {isEditing && !isMe && !u.system_admin
                      ? <RoleSelect testid={`user-edit-role-${u.id}`} value={draft.role}
                                    onChange={(r) => setDraft({ ...draft, role: r })} />
                      : (u.role ? ROLE_LABEL[u.role] ?? u.role : "No role")}
                  </td>
                  <td style={CELL} data-testid={`user-active-${u.id}`}>{u.active ? "Yes" : "No"}</td>
                  <td style={{ ...CELL, fontVariantNumeric: "tabular-nums" }} data-testid={`user-signin-${u.id}`}
                      title={u.last_sign_in ? `${u.last_sign_in} UTC` : undefined}>
                    {formatSignIn(u.last_sign_in)}
                  </td>
                  <td style={{ ...CELL, textAlign: "right" }}>
                    <span style={{ display: "inline-flex", gap: spacing[6] }}>
                      {isEditing ? (
                        <>
                          <button data-testid={`user-edit-save-${u.id}`} style={PRIMARY} onClick={() => void saveEdit(u)}>Save</button>
                          <button data-testid={`user-edit-cancel-${u.id}`} style={BUTTON} onClick={() => setEditing(null)}>Cancel</button>
                        </>
                      ) : (
                        <button data-testid={`user-edit-${u.id}`} style={BUTTON} onClick={() => startEdit(u)}>Edit</button>
                      )}
                      {/* Reset: never your own row (use "Change my password"). */}
                      {!isMe && !isEditing && resetting !== u.id && (
                        <button data-testid={`user-reset-${u.id}`} style={BUTTON}
                                onClick={() => { setResetting(u.id); setResetPw(""); setRowError(null); }}>
                          Reset password
                        </button>
                      )}
                      {resetting === u.id && (
                        <>
                          <input data-testid={`user-reset-password-${u.id}`} type="password"
                                 autoComplete="new-password" placeholder={`New password (min ${MIN_PASSWORD})`}
                                 value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                                 style={{ ...INPUT, width: 200 }} />
                          <button data-testid={`user-reset-save-${u.id}`} style={PRIMARY} onClick={() => void saveReset(u)}>Set</button>
                          <button data-testid={`user-reset-cancel-${u.id}`} style={BUTTON}
                                  onClick={() => { setResetting(null); setResetPw(""); }}>Cancel</button>
                        </>
                      )}
                      {/* Never offered on your own row; the server refuses it anyway. */}
                      {!isMe && !isEditing && (
                        <button data-testid={`user-toggle-active-${u.id}`} style={BUTTON}
                                onClick={() => void toggleActive(u)}>
                          {u.active ? "Deactivate" : "Reactivate"}
                        </button>
                      )}
                    </span>
                    {rowError?.id === u.id && (
                      <div data-testid={`user-error-${u.id}`} role="alert"
                           style={{ whiteSpace: "normal", marginTop: spacing[4], fontSize: typography.fontSize.xs, color: semanticColors.destructive }}>
                        {rowError.text}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
